import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Layout, Card, BlockStack, InlineStack, Text, TextField, Checkbox, Button, Banner, Box, DropZone, Badge, Divider, Spinner, ProgressBar, List } from '@shopify/polaris';
import { PlusIcon, MagicIcon, ClipboardIcon, SaveIcon } from '@shopify/polaris-icons';
import ProjectSelect from '../../../shared/components/projectSelect.jsx';
import CabinetCard from './CabinetCard.jsx';
import { api, formatEur, formatDate, cabinetToSpec, cabinetFromSpec, newCabinet, newKey, GROUP_LABELS, STARTER_OPTIONS, IO_LABELS } from './shared.js';

const GROUP_ORDER = ['enclosure', 'power', 'plc', 'safety', 'motors', 'machine', 'engineering', 'delivery'];
const GROUP_COLORS = {
  enclosure: '#5C6AC4',
  power: '#47C1BF',
  plc: '#006FBB',
  safety: '#DE3618',
  motors: '#F49342',
  machine: '#B98900',
  engineering: '#9C6ADE',
  delivery: '#8A8A8A'
};

function isBlank(cabinets) {
  return cabinets.length === 1 && !cabinets[0].motors.length && Object.values(cabinets[0].io).every((v) => !v);
}

function summaryText({ name, result, cabinets }) {
  const lines = [`Cabinet estimate${name ? `: ${name}` : ''} (Elektro Tech price level, ${formatDate(result.priceLevelDate)})`];
  result.cabinets.forEach((c, i) => {
    const r = c.resolved;
    const io = Object.entries(r.io || {})
      .filter(([, n]) => n)
      .map(([type, n]) => `${n} ${IO_LABELS[type]}`)
      .join(', ');
    const motors = (cabinets[i]?.motors || [])
      .filter((m) => m.kw)
      .map((m) => `${Number(m.qty) > 1 ? `${m.qty}× ` : ''}${m.kw} kW ${STARTER_OPTIONS.find((o) => o.value === m.starter)?.label || m.starter}`)
      .join(', ');
    lines.push(`- ${c.name}: ${formatEur(c.total)} (${r.kind === 'wall' ? 'wall-mounted' : `floor ${r.widthMm} mm`}, ${r.incomingA} A${io ? `, ${io}` : ''}${motors ? `; ${motors}` : ''})`);
  });
  if (result.machine?.total) lines.push(`- Machine wiring: ${formatEur(result.machine.total)}`);
  if (result.engineering.total) lines.push(`- Engineering: ${formatEur(result.engineering.total)}`);
  if (result.delivery.total) lines.push(`- Delivery: ${formatEur(result.delivery.total)}`);
  lines.push(`Total supplier price: ${formatEur(result.total)} (likely ${formatEur(result.low)} – ${formatEur(result.high)})`);
  lines.push(`With ${result.markupPct}% markup: ${formatEur(result.selling.total)}`);
  return lines.join('\n');
}

function GroupBars({ groups, total }) {
  const entries = GROUP_ORDER.filter((g) => groups?.[g] > 0).map((g) => [g, groups[g]]);
  if (!entries.length || !total) return null;
  return (
    <BlockStack gap="200">
      <div style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden' }} aria-hidden="true">
        {entries.map(([g, value]) => (
          <div key={g} style={{ width: `${(value / total) * 100}%`, background: GROUP_COLORS[g] }} />
        ))}
      </div>
      {entries.map(([g, value]) => (
        <InlineStack key={g} align="space-between" blockAlign="center">
          <InlineStack gap="200" blockAlign="center">
            <span style={{ width: 10, height: 10, borderRadius: 2, background: GROUP_COLORS[g], display: 'inline-block' }} />
            <Text as="span" variant="bodySm">
              {GROUP_LABELS[g]}
            </Text>
          </InlineStack>
          <Text as="span" variant="bodySm">
            {formatEur(value)}
          </Text>
        </InlineStack>
      ))}
    </BlockStack>
  );
}

function ImportCard({ onImported, aiEnabled }) {
  const [files, setFiles] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const run = async () => {
    setBusy(true);
    setError('');
    try {
      const form = new FormData();
      files.forEach((file) => form.append('files', file));
      if (text.trim()) form.append('text', text.trim());
      const result = await api('/import', { method: 'POST', body: form });
      if (!result.cabinets?.length) throw new Error('No cabinets found in these files');
      onImported(result);
      setFiles([]);
      setText('');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <BlockStack gap="300">
        <BlockStack gap="100">
          <Text as="h2" variant="headingMd">
            Start from your IO list and motor list
          </Text>
          <Text as="p" tone="subdued">
            Drop the same Excel files you send to Elektro Tech (PLC IO list, motor list, cabinet summary), or describe the cabinet in a few sentences. The cabinets below are filled in for you.
          </Text>
        </BlockStack>
        <DropZone accept=".xlsx,.xls,.xlsm,.csv" allowMultiple variableHeight onDrop={(dropped) => setFiles((current) => [...current, ...dropped])} label="IO list / motor list" labelHidden>
          {files.length ? (
            <Box padding="300">
              <BlockStack gap="100">
                {files.map((file, i) => (
                  <InlineStack key={`${file.name}-${i}`} align="space-between">
                    <Text as="span">{file.name}</Text>
                    <Button variant="plain" onClick={() => setFiles((current) => current.filter((_, j) => j !== i))}>
                      Remove
                    </Button>
                  </InlineStack>
                ))}
              </BlockStack>
            </Box>
          ) : (
            <DropZone.FileUpload actionTitle="Add Excel files" actionHint="IO list, motor list (.xlsx)" />
          )}
        </DropZone>
        <TextField
          label="…or describe it"
          multiline={3}
          value={text}
          onChange={setText}
          placeholder="e.g. Small stand-alone box on 230 V: 3 DI, 2 DO, main switch, 24 VDC 2 A supply. PLC supplied by us."
          autoComplete="off"
          helpText={aiEnabled ? 'Descriptions and non-standard lists are read by AI.' : 'AI is not configured: only standard Excel lists can be imported.'}
        />
        {error && (
          <Banner tone="critical" onDismiss={() => setError('')}>
            {error}
          </Banner>
        )}
        <InlineStack align="end">
          <Button variant="primary" icon={MagicIcon} loading={busy} disabled={!files.length && !text.trim()} onClick={run}>
            Fill in cabinets
          </Button>
        </InlineStack>
      </BlockStack>
    </Card>
  );
}

export default function EstimateTab({ overview, loaded, onSaved, notify }) {
  const defaults = overview?.defaults || {};
  const [name, setName] = useState('');
  const [estimateId, setEstimateId] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [cabinets, setCabinets] = useState(() => [newCabinet(0)]);
  const [ioReservePct, setIoReservePct] = useState('');
  const [markupPct, setMarkupPct] = useState('');
  const [includeEngineering, setIncludeEngineering] = useState(true);
  const [includeDelivery, setIncludeDelivery] = useState(true);
  const [includeMachineWiring, setIncludeMachineWiring] = useState(false);
  const [result, setResult] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [error, setError] = useState('');
  const [importNotes, setImportNotes] = useState(null);
  const [saving, setSaving] = useState(false);
  const abortRef = useRef(null);

  // open a saved estimate
  useEffect(() => {
    if (!loaded) return;
    const input = loaded.input || {};
    setName(loaded.Name || '');
    setEstimateId(loaded.EstimateID || null);
    setProjectId(loaded.ProjectID || null);
    setCabinets((input.cabinets || []).length ? input.cabinets.map((c, i) => cabinetFromSpec(c, i)) : [newCabinet(0)]);
    setIoReservePct(input.ioReservePct == null ? '' : String(input.ioReservePct));
    setMarkupPct(input.markupPct == null ? '' : String(input.markupPct));
    setIncludeEngineering(input.includeEngineering !== false);
    setIncludeDelivery(input.includeDelivery !== false);
    setIncludeMachineWiring(input.includeMachineWiring === true);
    setImportNotes(null);
  }, [loaded]);

  const spec = useMemo(
    () => ({
      cabinets: cabinets.map(cabinetToSpec),
      includeEngineering,
      includeDelivery,
      includeMachineWiring,
      ioReservePct: ioReservePct === '' ? null : Number(ioReservePct),
      markupPct: markupPct === '' ? null : Number(markupPct)
    }),
    [cabinets, includeEngineering, includeDelivery, includeMachineWiring, ioReservePct, markupPct]
  );

  useEffect(() => {
    const timer = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setEstimating(true);
      try {
        const next = await api('/estimate', { method: 'POST', body: { spec }, signal: controller.signal });
        setResult(next);
        setError('');
      } catch (err) {
        if (err.name !== 'AbortError') setError(err.message);
      } finally {
        if (abortRef.current === controller) setEstimating(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [spec]);

  const updateCabinet = useCallback((key, next) => setCabinets((current) => current.map((c) => (c.key === key ? next : c))), []);

  const onImported = (imported) => {
    const next = imported.cabinets.map((c, i) => cabinetFromSpec(c, i));
    setCabinets((current) => (isBlank(current) ? next : [...current, ...next.map((c) => ({ ...c, plcCpu: false }))]));
    setImportNotes({ notes: imported.notes || [], usedAi: imported.usedAi, count: next.length });
  };

  const save = async () => {
    if (!name.trim()) {
      notify('Give the estimate a name first', true);
      return;
    }
    setSaving(true);
    try {
      const saved = await api('/estimates', { method: 'POST', body: { estimateId, name: name.trim(), projectId, spec } });
      setEstimateId(saved.EstimateID);
      notify(estimateId ? 'Estimate updated' : 'Estimate saved');
      onSaved?.();
    } catch (err) {
      notify(err.message, true);
    } finally {
      setSaving(false);
    }
  };

  const copySummary = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(summaryText({ name, result, cabinets }));
      notify('Summary copied');
    } catch (err) {
      notify('Could not copy to the clipboard', true);
    }
  };

  const startNew = () => {
    setName('');
    setEstimateId(null);
    setProjectId(null);
    setCabinets([newCabinet(0)]);
    setImportNotes(null);
  };

  const model = overview?.model;
  const resultByIndex = result?.cabinets || [];

  return (
    <Layout>
      <Layout.Section>
        <BlockStack gap="400">
          <ImportCard onImported={onImported} aiEnabled={overview?.aiEnabled} />
          {importNotes && (
            <Banner
              tone="success"
              title={`${importNotes.count} cabinet${importNotes.count === 1 ? '' : 's'} filled in${importNotes.usedAi ? ' (read by AI: check the counts)' : ''}`}
              onDismiss={() => setImportNotes(null)}
            >
              {importNotes.notes.length > 0 && (
                <List>
                  {importNotes.notes.slice(0, 12).map((note, i) => (
                    <List.Item key={i}>{note}</List.Item>
                  ))}
                </List>
              )}
            </Banner>
          )}

          <Card>
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">
                  Project
                </Text>
                {estimateId && (
                  <Button variant="plain" onClick={startNew}>
                    Start a new estimate
                  </Button>
                )}
              </InlineStack>
              <InlineStack gap="300" wrap>
                <Box minWidth="260px">
                  <TextField label="Estimate name" value={name} onChange={setName} placeholder="e.g. Cafés Richard – roasting line" autoComplete="off" />
                </Box>
                <Box minWidth="300px">
                  <ProjectSelect label="Project (optional)" value={projectId} onChange={(value) => setProjectId(value ?? null)} />
                </Box>
              </InlineStack>
              <InlineStack gap="300" blockAlign="start" wrap>
                <Box width="150px">
                  <TextField
                    label="IO reserve"
                    type="number"
                    suffix="%"
                    value={ioReservePct}
                    placeholder={String(defaults.ioReservePct ?? 10)}
                    onChange={setIoReservePct}
                    helpText="Spare channels, rounded to modules"
                    autoComplete="off"
                  />
                </Box>
                <Box width="150px">
                  <TextField
                    label="Our markup"
                    type="number"
                    suffix="%"
                    value={markupPct}
                    placeholder={String(defaults.markupPct ?? 25)}
                    onChange={setMarkupPct}
                    helpText="On the supplier price"
                    autoComplete="off"
                  />
                </Box>
                <BlockStack gap="100">
                  <Checkbox label="Include engineering (EPLAN)" checked={includeEngineering} onChange={setIncludeEngineering} />
                  <Checkbox label="Include delivery to Schuilenburg" checked={includeDelivery} onChange={setIncludeDelivery} />
                  <Checkbox
                    label="Elektro Tech also wires the machine"
                    helpText="Cables to the motors and sensors on the machine (Roastworks machines)"
                    checked={includeMachineWiring}
                    onChange={setIncludeMachineWiring}
                  />
                </BlockStack>
              </InlineStack>
            </BlockStack>
          </Card>

          {cabinets.map((cabinet, index) => (
            <CabinetCard
              key={cabinet.key}
              cabinet={cabinet}
              index={index}
              result={resultByIndex[index]}
              canRemove={cabinets.length > 1}
              onChange={(next) => updateCabinet(cabinet.key, next)}
              onDuplicate={() =>
                setCabinets((current) => {
                  const copy = { ...cabinet, key: newKey(), name: `${cabinet.name} (copy)`, plcCpu: false, motors: cabinet.motors.map((m) => ({ ...m, key: newKey() })) };
                  const next = [...current];
                  next.splice(index + 1, 0, copy);
                  return next;
                })
              }
              onRemove={() => setCabinets((current) => current.filter((c) => c.key !== cabinet.key))}
            />
          ))}
          <InlineStack>
            <Button icon={PlusIcon} onClick={() => setCabinets((current) => [...current, newCabinet(current.length, { plcCpu: false })])}>
              Add cabinet
            </Button>
          </InlineStack>
        </BlockStack>
      </Layout.Section>

      <Layout.Section variant="oneThird">
        <div style={{ position: 'sticky', top: 16 }}>
          <BlockStack gap="400">
            <Card>
              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="h2" variant="headingMd">
                    Elektro Tech price
                  </Text>
                  {estimating && <Spinner size="small" accessibilityLabel="Calculating" />}
                </InlineStack>
                {error && <Banner tone="critical">{error}</Banner>}
                {result && (
                  <>
                    <BlockStack gap="100">
                      <div data-testid="estimate-total">
                        <Text as="p" variant="heading2xl">
                          {formatEur(result.total)}
                        </Text>
                      </div>
                      <Text as="p" tone="subdued">
                        Likely between {formatEur(result.low)} and {formatEur(result.high)}
                      </Text>
                    </BlockStack>
                    <Box>
                      <ProgressBar progress={result.high > 0 ? Math.round(Math.min(100, (result.total / result.high) * 100)) : 0} size="small" />
                    </Box>
                    <Divider />
                    <BlockStack gap="150">
                      {result.cabinets.map((c) => (
                        <InlineStack key={c.index} align="space-between">
                          <Text as="span">{c.name}</Text>
                          <Text as="span">{formatEur(c.total)}</Text>
                        </InlineStack>
                      ))}
                      {result.machine?.total > 0 && (
                        <InlineStack align="space-between">
                          <Text as="span">Machine wiring</Text>
                          <Text as="span">{formatEur(result.machine.total)}</Text>
                        </InlineStack>
                      )}
                      {result.engineering.total > 0 && (
                        <InlineStack align="space-between">
                          <Text as="span">Engineering</Text>
                          <Text as="span">{formatEur(result.engineering.total)}</Text>
                        </InlineStack>
                      )}
                      {result.delivery.total > 0 && (
                        <InlineStack align="space-between">
                          <Text as="span">Delivery</Text>
                          <Text as="span">{formatEur(result.delivery.total)}</Text>
                        </InlineStack>
                      )}
                    </BlockStack>
                    <Divider />
                    <GroupBars groups={result.groups} total={result.total} />
                    <Divider />
                    <BlockStack gap="100">
                      <InlineStack align="space-between" blockAlign="center">
                        <Text as="span" variant="headingSm">
                          Our price (+{result.markupPct}%)
                        </Text>
                        <Text as="span" variant="headingLg">
                          {formatEur(result.selling.total)}
                        </Text>
                      </InlineStack>
                      <Text as="p" variant="bodySm" tone="subdued">
                        Margin {formatEur(result.selling.margin)} · range {formatEur(result.selling.low)} – {formatEur(result.selling.high)}
                      </Text>
                    </BlockStack>
                    {result.warnings?.length > 0 && (
                      <Banner tone={result.warnings.some((w) => w.level === 'warning') ? 'warning' : 'info'}>
                        <List>
                          {result.warnings.map((w, i) => (
                            <List.Item key={i}>{w.text}</List.Item>
                          ))}
                        </List>
                      </Banner>
                    )}
                    <InlineStack gap="200">
                      <Button variant="primary" icon={SaveIcon} loading={saving} onClick={save} disabled={!overview?.tablesAvailable}>
                        {estimateId ? 'Update estimate' : 'Save estimate'}
                      </Button>
                      <Button icon={ClipboardIcon} onClick={copySummary}>
                        Copy summary
                      </Button>
                    </InlineStack>
                  </>
                )}
              </BlockStack>
            </Card>
            {model && (
              <Card>
                <BlockStack gap="200">
                  <InlineStack gap="200" blockAlign="center">
                    <Text as="h3" variant="headingSm">
                      How this is calculated
                    </Text>
                    {model.trained ? <Badge tone="success">Trained</Badge> : <Badge tone="attention">Default prices</Badge>}
                  </InlineStack>
                  <Text as="p" variant="bodySm" tone="subdued">
                    {model.trained
                      ? `Learned from ${
                          model.metrics.quoteSources || model.metrics.invoiceSources
                            ? `${model.metrics.quoteSources} Elektro Tech quotes and ${model.metrics.invoiceSources} post-calculated invoices`
                            : `${model.metrics.sources} Elektro Tech documents`
                        }, prices brought to the level of ${formatDate(model.referenceDate)}.`
                      : 'Built-in Elektro Tech price level 2026 until a super admin trains the model on their quotes and invoices.'}
                    {model.metrics.leaveOneOut?.medianAbsErrorPct != null &&
                      ` On ${model.metrics.leaveOneOut.basis === 'quotes' ? 'quotes' : 'documents'} it did not learn from, the estimate was off by ${
                        model.metrics.leaveOneOut.medianAbsErrorPct
                      }% (median of ${model.metrics.leaveOneOut.documents}).`}
                  </Text>
                  {model.quoteVersusInvoiced?.medianDifferencePct != null && model.quoteVersusInvoiced.projects > 0 && (
                    <Text as="p" variant="bodySm" tone="subdued">
                      Their quotes are estimates on time-and-material basis: final invoices differed by {model.quoteVersusInvoiced.medianDifferencePct > 0 ? '+' : ''}
                      {model.quoteVersusInvoiced.medianDifferencePct}% (median of {model.quoteVersusInvoiced.projects} projects).
                    </Text>
                  )}
                </BlockStack>
              </Card>
            )}
          </BlockStack>
        </div>
      </Layout.Section>
    </Layout>
  );
}
